import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { attemptAsync } from 'es-toolkit/util';

// Fallback for when `keyboardDidHide` never fires (e.g. a hardware keyboard).
const KEYBOARD_HIDE_TIMEOUT_MS = 1000;

// Tracked from keyboard events rather than focus: tapping a non-input element
// blurs the input immediately, but the keyboard (and the shrunk viewport)
// stays up until its hide animation finishes.
let isNativeKeyboardOpen = false;

if (Capacitor.isNativePlatform()) {
  // On failure, `isNativeKeyboardOpen` stays false and hiding is skipped.
  Keyboard.addListener('keyboardDidShow', () => {
    isNativeKeyboardOpen = true;
  }).catch(() => {});
  Keyboard.addListener('keyboardDidHide', () => {
    isNativeKeyboardOpen = false;
  }).catch(() => {});
}

/**
 * Hides the on-screen keyboard and resolves once the viewport is back to full
 * height. Await this before opening layout that measures `window.innerHeight`,
 * which is keyboard-shrunk while the keyboard is up.
 */
export const hideKeyboardAndWait = async (): Promise<void> => {
  if (!Capacitor.isNativePlatform() || !isNativeKeyboardOpen) {
    return;
  }

  let onHide = (): void => {};
  const didHide = new Promise<void>((resolve) => {
    onHide = resolve;
    setTimeout(resolve, KEYBOARD_HIDE_TIMEOUT_MS);
  });

  // Hiding is best-effort: on a plugin failure, fall through to the timeout
  // rather than rejecting and blocking the caller.
  // Register before hiding so the event can't fire first.
  const [, listener] = await attemptAsync(() =>
    Keyboard.addListener('keyboardDidHide', () => onHide()),
  );

  // The keyboard may already be hiding after a blur; hiding again is harmless.
  await attemptAsync(() => Keyboard.hide());
  await didHide;
  await attemptAsync(async () => listener?.remove());
};
